/**
 * Mention placeholders for the AI report review (AI_AGENT_SYSTEM_SPEC.md §2.3).
 *
 * The report editor (TipTap) contains mention nodes for colleagues and contacts;
 * mentions drive notifications, so an AI rewrite must never drop them. Before a
 * draft is sent, every mention node becomes a placeholder like `[[m:1]]` and the
 * original node is kept locally. A rewrite can only be applied when it contains
 * exactly the same placeholders, which are then turned back into mention nodes.
 * Side effect: names inside mentions never reach the AI provider.
 *
 * Known limitation: a rewrite keeps paragraphs, line breaks and mentions, but not
 * inline formatting (bold, italic) or block styling (quotes, lists).
 */

export const MENTION_NODE_TYPES = ["userMention", "contactMention"];

const PLACEHOLDER_PATTERN = /\[\[m:(\d+)\]\]/g;
const PARAGRAPH_SEPARATOR = "\n\n";

/**
 * Serialises a TipTap JSON document to plain text with mention placeholders.
 * @param {object} doc - TipTap JSON (`editor.getJSON()`)
 * @returns {{ text: string, mentions: Record<string, object> }} the text and the
 *   original mention nodes by placeholder number
 */
export function docToPlaceholderText(doc) {
  const mentions = {};
  let counter = 0;

  const inline = (nodes = []) =>
    nodes
      .map((node) => {
        if (MENTION_NODE_TYPES.includes(node.type)) {
          counter += 1;
          mentions[counter] = node;
          return `[[m:${counter}]]`;
        }
        if (node.type === "text") return node.text || "";
        if (node.type === "hardBreak") return "\n";
        return inline(node.content);
      })
      .join("");

  const blocks = (nodes = []) =>
    nodes.flatMap((node) => {
      const children = node.content || [];
      const hasBlockChildren = children.some(
        (child) =>
          child.type !== "text" &&
          child.type !== "hardBreak" &&
          !MENTION_NODE_TYPES.includes(child.type),
      );
      return hasBlockChildren ? blocks(children) : [inline(children)];
    });

  const text = blocks(doc?.content)
    .map((block) => block.trim())
    .filter(Boolean)
    .join(PARAGRAPH_SEPARATOR);

  return { text, mentions };
}

/**
 * Sorted list of the placeholders in a text, e.g. ["[[m:1]]", "[[m:2]]"].
 * @param {string} text
 * @returns {string[]}
 */
export function placeholdersIn(text) {
  return (String(text || "").match(PLACEHOLDER_PATTERN) || []).sort();
}

/**
 * True when both texts contain exactly the same placeholders (each equally often).
 * @param {string} original
 * @param {string} rewrite
 * @returns {boolean}
 */
export function hasSamePlaceholders(original, rewrite) {
  const a = placeholdersIn(original);
  const b = placeholdersIn(rewrite);
  return (
    a.length === b.length && a.every((placeholder, i) => placeholder === b[i])
  );
}

/**
 * Readable preview of a rewrite: placeholders shown as `@Name` (users) or
 * `#Name` (contacts). Display only; never send this text anywhere.
 * @param {string} text - rewrite containing placeholders
 * @param {Record<string, object>} mentions - from docToPlaceholderText
 * @returns {string}
 */
export function placeholderTextToPreview(text, mentions) {
  return String(text || "").replace(
    PLACEHOLDER_PATTERN,
    (placeholder, number) => {
      const mention = mentions?.[number];
      if (!mention) return placeholder;
      const prefix = mention.type === "contactMention" ? "#" : "@";
      return `${prefix}${mention.attrs?.label || mention.attrs?.id || ""}`;
    },
  );
}

/**
 * Builds a TipTap JSON document from a rewrite, restoring mention nodes.
 * Paragraphs are separated by blank lines; single newlines become hard breaks.
 * @param {string} text - rewrite containing placeholders
 * @param {Record<string, object>} mentions - from docToPlaceholderText
 * @returns {object|null} TipTap JSON, or null if the text references a mention that
 *   does not exist (never apply such a rewrite)
 */
export function placeholderTextToDoc(text, mentions) {
  let unknownMention = false;

  const inlineNodes = (line) => {
    const nodes = [];
    let lastIndex = 0;
    for (const match of line.matchAll(PLACEHOLDER_PATTERN)) {
      if (match.index > lastIndex)
        nodes.push({ type: "text", text: line.slice(lastIndex, match.index) });
      const mention = mentions?.[match[1]];
      // JSON copy: mentions may be Vue reactive proxies, which structuredClone rejects.
      if (mention) nodes.push(JSON.parse(JSON.stringify(mention)));
      else unknownMention = true;
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length)
      nodes.push({ type: "text", text: line.slice(lastIndex) });
    return nodes;
  };

  const paragraphs = String(text || "")
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      const content = paragraph
        .split("\n")
        .flatMap((line, i) =>
          i === 0
            ? inlineNodes(line)
            : [{ type: "hardBreak" }, ...inlineNodes(line)],
        );
      return content.length
        ? { type: "paragraph", content }
        : { type: "paragraph" };
    });

  if (unknownMention) return null;
  return {
    type: "doc",
    content: paragraphs.length ? paragraphs : [{ type: "paragraph" }],
  };
}

/**
 * Finds where a phrase occurs in the editor's text, for replacing it with a mention.
 * Only matches inside a single text node (a phrase that is partly bold, or already
 * a mention, is not found).
 * @param {{ text: string, pos: number }[]} textNodes - text nodes with their document position
 * @param {string} phrase
 * @returns {{ from: number, to: number }|null} document range of the first occurrence
 */
export function findPhraseRange(textNodes, phrase) {
  if (!phrase) return null;
  for (const node of textNodes || []) {
    const index = (node.text || "").indexOf(phrase);
    if (index !== -1) {
      const from = node.pos + index;
      return { from, to: from + phrase.length };
    }
  }
  return null;
}

/**
 * Keys ("User:5", "Contact:3") of the mentions already in a draft, in the form the
 * API expects, so they are neither suggested again nor counted as missed.
 * @param {Record<string, object>} mentions - from docToPlaceholderText
 * @returns {{ type: string, id: number|string }[]}
 */
export function existingMentions(mentions) {
  return Object.values(mentions || {})
    .map((node) => ({
      type: node.type === "contactMention" ? "Contact" : "User",
      id: node.attrs?.id,
    }))
    .filter((mention) => mention.id && mention.id !== "*");
}
